import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { PhotoPositionEditor, type PhotoFocus } from '../ui/PhotoPositionEditor';

interface ImageTitleEditModalProps {
  isOpen: boolean;
  initialTitle: string;
  onClose: () => void;
  imageUrl?: string;
  initialFocal?: PhotoFocus;
  onSave: (title: string, focal: PhotoFocus) => Promise<void>;
}

export const ImageTitleEditModal: React.FC<ImageTitleEditModalProps> = ({
  isOpen,
  initialTitle,
  onClose,
  onSave, imageUrl, initialFocal,
}) => {
  const [focal, setFocal] = useState<PhotoFocus>({ x: 0.5, y: 0.5 });
  const [title, setTitle] = useState(initialTitle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setFocal({ x: initialFocal?.x ?? 0.5, y: initialFocal?.y ?? 0.5, zoom: initialFocal?.zoom ?? 1 });
      setError(null);
    }
  }, [isOpen, initialTitle, initialFocal?.x, initialFocal?.y, initialFocal?.zoom]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Title cannot be empty');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, focal);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save title');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit photo" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="image-title" className="input-label">
            Photo title
          </label>
          <input
            id="image-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input w-full"
            maxLength={120}
            autoFocus
          />
        </div>
        {imageUrl && <PhotoPositionEditor src={imageUrl} value={focal} onChange={setFocal} />}
        {error && (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
